import { useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';

import type { CreateTreatmentRequest, Treatment } from '../types';

const DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 75, 90, 105, 120, 150, 180];

interface AddTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTreatmentRequest) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  treatment?: Treatment;
}

export function AddTreatmentModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
  treatment,
}: AddTreatmentModalProps) {
  const { t } = useTranslation();
  const isEdit = !!treatment;

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('specialties.treatments.modal.nameRequired'))
          .max(100, t('specialties.treatments.modal.nameTooLong')),
        description: z.string().max(1000).optional(),
        durationMinutes: z.coerce
          .number({ invalid_type_error: t('specialties.treatments.modal.durationRequired') })
          .refine((v) => DURATION_OPTIONS.includes(v), {
            message: t('specialties.treatments.modal.durationRequired'),
          }),
        price: z.coerce
          .number({ invalid_type_error: t('specialties.treatments.modal.priceRequired') })
          .min(0, t('specialties.treatments.modal.priceMin')),
        currency: z
          .string()
          .min(1, t('specialties.treatments.modal.currencyRequired'))
          .regex(/^[A-Z]{3}$/, t('specialties.treatments.modal.currencyInvalid')),
        onlineBookingEnabled: z.boolean().optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      durationMinutes: 30,
      price: 0,
      currency: 'RON',
      onlineBookingEnabled: true,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        treatment
          ? {
              name: treatment.name,
              description: treatment.description ?? '',
              durationMinutes: treatment.durationMinutes,
              price: treatment.price,
              currency: treatment.currency,
              onlineBookingEnabled: treatment.onlineBookingEnabled,
            }
          : {
              name: '',
              description: '',
              durationMinutes: 30,
              price: 0,
              currency: 'RON',
              onlineBookingEnabled: true,
            },
      );
    }
  }, [isOpen, treatment, reset]);

  function onFormSubmit(values: FormValues) {
    onSubmit({
      name: values.name,
      description: values.description || null,
      durationMinutes: values.durationMinutes,
      price: values.price,
      currency: values.currency,
      onlineBookingEnabled: values.onlineBookingEnabled ?? true,
    });
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEdit
          ? t('specialties.treatments.modal.editTitle')
          : t('specialties.treatments.modal.addTitle')
      }
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('specialties.treatments.modal.cancel')}
          </Button>
          <Button type="submit" form="add-treatment-form" isLoading={isSubmitting}>
            {t('specialties.treatments.modal.submit')}
          </Button>
        </>
      }
    >
      {errorMessage && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}
      <form
        id="add-treatment-form"
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
        className="space-y-4"
      >
        <Input
          label={t('specialties.treatments.modal.nameLabel')}
          placeholder={t('specialties.treatments.modal.namePlaceholder')}
          error={errors.name?.message}
          autoFocus
          {...register('name')}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('specialties.treatments.modal.descriptionLabel')}
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              {t('specialties.treatments.modal.optional')}
            </span>
          </label>
          <textarea
            placeholder={t('specialties.treatments.modal.descriptionPlaceholder')}
            rows={2}
            className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            {...register('description')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('specialties.treatments.modal.durationLabel')}
          </label>
          <select
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            {...register('durationMinutes', { valueAsNumber: true })}
          >
            {DURATION_OPTIONS.map((min) => (
              <option key={min} value={min}>
                {t('specialties.treatments.duration', { minutes: min })}
              </option>
            ))}
          </select>
          {errors.durationMinutes && (
            <p className="text-xs text-destructive">{errors.durationMinutes.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('specialties.treatments.modal.priceLabel')}
            placeholder={t('specialties.treatments.modal.pricePlaceholder')}
            type="number"
            min="0"
            step="0.01"
            error={errors.price?.message}
            {...register('price', { valueAsNumber: true })}
          />
          <Input
            label={t('specialties.treatments.modal.currencyLabel')}
            placeholder={t('specialties.treatments.modal.currencyPlaceholder')}
            maxLength={3}
            error={errors.currency?.message}
            {...register('currency', {
              onChange: (e) => {
                e.target.value = e.target.value.toUpperCase();
              },
            })}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
            {...register('onlineBookingEnabled')}
          />
          {t('specialties.treatments.modal.onlineBookingLabel')}
        </label>
      </form>
    </Modal>
  );
}
